/**
 * Organization SETTINGS tab (org_settings): country/tz/currencies/scale/alert
 * threshold + branding + feature overrides. Local-first writing →
 * write-path (owner/org_admin authz). Local labels come from the country
 * pack.
 *
 * AUTONOMY:
 *  · The grading scale uses the REAL keys from the shared registry (there
 *    used to be a local literal with nonexistent keys that silently fell
 *    back to default).
 *  · The exchange rate is managed ONLY in Finance → Rates (daily history);
 *    here only the last cached one is shown, with no second write-path.
 *  · Branding (public name, used e.g. on receipts) and org/course feature
 *    overrides (registry-driven, course→org→platform semantics).
 */
import { msg } from "@lingui/core/macro";
import { createEffect, createSignal, For, Show, untrack } from "solid-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, TextField, Skeleton } from "@fleet/ui";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { InfoHint } from "@/components/ui/info-hint";
import { useI18n } from "@/providers/i18n-provider";
import { Link } from "@tanstack/solid-router";
import {
  dependentsOf,
  featureGraph,
  getCountryPack,
  listCountryPacks,
  modelShapeFromRow,
  modelStrategy,
  orgConfigurableFeatures,
  type OrgSettingsInput,
  parseFeatureOverrides,
  parseRequireClause,
  resolveFeatureEffective,
  saveOrgSettingsMutation,
  serializeFeatureOverrides,
  validateOrgOverridesChange,
} from "@space/shared";
// FLAGS: confirmation with implications + graph validation before applying.
import {
  FeatureToggleDialog,
  type FeatureToggleRequest,
} from "@/features/flags/feature-toggle-dialog";
import { usePlatformFlags } from "@/features/flags/use-feature-enabled";
import { useAcademyWrite, useGradingModels, useOrgSettings } from "../hooks/use-academy-data";

interface BrandingShape {
  publicName?: string;
  tagline?: string;
}

function parseBranding(raw: string | null | undefined): BrandingShape {
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as BrandingShape)
      : {};
  } catch {
    return {};
  }
}

/** State of the per-feature override select. */
type OverrideChoice = "default" | "on" | "off";

function toOverrideChoice(v: string): OverrideChoice {
  return v === "on" || v === "off" ? v : "default";
}

export function OrgSettingsTab(props: { orgId: string }) {
  const { t } = useI18n();
  const settings = useOrgSettings(() => props.orgId);
  const { drizzle } = useAcademyWrite();
  // Group E: the org's default scale is chosen among its MODELS (sync).
  const gradingModels = useGradingModels(() => props.orgId);
  const modelSummary = (m: {
    kind: string | null;
    max_value: number | null;
    passing_threshold: number | null;
    decimals: number | null;
    letter_bands: unknown;
    display_suffix: string | null;
    system_key: string | null;
  }): string => {
    const shape = modelShapeFromRow({
      id: "x",
      kind: m.kind ?? "points",
      max_value: m.max_value ?? 0,
      passing_threshold: m.passing_threshold ?? 0,
      decimals: m.decimals ?? 0,
      letter_bands: m.letter_bands,
      display_suffix: m.display_suffix,
      system_key: m.system_key,
    });
    const strat = modelStrategy(shape);
    return `${strat.displayValue(shape.passingThreshold * shape.maxValue)} / ${strat.displayValue(shape.maxValue)}`;
  };

  const [country, setCountry] = createSignal("VE");
  const [timezone, setTimezone] = createSignal("America/Caracas");
  const [locale, setLocale] = createSignal("es");
  const [currencyPrimary, setCurrencyPrimary] = createSignal("USD");
  const [currencySecondary, setCurrencySecondary] = createSignal("VES");
  const [gradingModelId, setGradingModelId] = createSignal("");
  const [threshold, setThreshold] = createSignal("3");
  const [publicName, setPublicName] = createSignal("");
  const [tagline, setTagline] = createSignal("");
  const [overrides, setOverrides] = createSignal<Record<string, boolean>>({});
  const [saving, setSaving] = createSignal(false);
  // FLAGS: override change pending confirmation (dialog).
  const [pendingChange, setPendingChange] = createSignal<
    (FeatureToggleRequest & { choice: OverrideChoice }) | null
  >(null);
  const platformFlags = usePlatformFlags();
  const graph = featureGraph();

  /**
   * CC-59 · Fields the user has TOUCHED in this form session.
   *
   * It's the only honest source of intent: what's rendered on screen might
   * be a code default because the `org_settings` row takes >15s to arrive
   * and "no row" is indistinguishable from "the row hasn't arrived yet".
   * Only what's touched is asserted on save (`asserted_fields`), so saving
   * the threshold can no longer trample the server's `features`. It also
   * happens to keep late hydration from overwriting what the user is editing.
   */
  const [touched, setTouched] = createSignal<ReadonlySet<string>>(new Set());
  const touch = (field: string) => {
    setTouched((prev) => (prev.has(field) ? prev : new Set(prev).add(field)));
  };
  /** Wraps a setter to mark its field as touched. */
  function edit<T>(field: string, apply: (value: T) => void): (value: T) => void {
    return (value) => {
      touch(field);
      apply(value);
    };
  }

  // Hydrates the form from the synced row — without trampling edits.
  createEffect(() => {
    const s = settings.data;
    if (!s) {
      return;
    }
    // `untrack`: hydration depends on the row, NOT on what the user
    // touches (otherwise every keystroke would re-trigger it and revert the edit).
    const dirty = untrack(touched);
    const fresh = (field: string) => !dirty.has(field);
    if (fresh("country")) {
      setCountry(s.country ?? "VE");
    }
    if (fresh("timezone")) {
      setTimezone(s.timezone ?? "America/Caracas");
    }
    if (fresh("locale")) {
      setLocale(s.locale ?? "es");
    }
    if (fresh("currency_primary")) {
      setCurrencyPrimary(s.currency_primary ?? "USD");
    }
    if (fresh("currency_secondary")) {
      setCurrencySecondary(s.currency_secondary ?? "VES");
    }
    if (fresh("grading_model_id")) {
      setGradingModelId(s.grading_model_id ?? "");
    }
    if (fresh("attendance_alert_threshold")) {
      setThreshold(String(s.attendance_alert_threshold ?? 3));
    }
    if (fresh("branding")) {
      const branding = parseBranding(s.branding);
      setPublicName(branding.publicName ?? "");
      setTagline(branding.tagline ?? "");
    }
    if (fresh("features")) {
      setOverrides({ ...parseFeatureOverrides(s.features) });
    }
  });

  const pack = () => getCountryPack(country());
  const configurableFeatures = orgConfigurableFeatures();

  const overrideChoice = (key: string): OverrideChoice => {
    const value = overrides()[key];
    return value === undefined ? "default" : value ? "on" : "off";
  };

  const applyOverrideChoice = (key: string, choice: OverrideChoice) => {
    touch("features");
    setOverrides((prev) => {
      const next = { ...prev };
      if (choice === "default") {
        delete next[key];
      } else {
        next[key] = choice === "on";
      }
      return next;
    });
  };

  /**
   * FLAGS: every toggle goes through the confirmation dialog with
   * implications + graph effects. HARD violations (same delta the server
   * validates in the write-path) block Confirm.
   */
  const setOverrideChoice = (key: string, choice: OverrideChoice) => {
    if (choice === overrideChoice(key)) {
      return;
    }
    const prev = overrides();
    const next = { ...prev };
    if (choice === "default") {
      delete next[key];
    } else {
      next[key] = choice === "on";
    }
    const violations = validateOrgOverridesChange({
      platform: platformFlags(),
      oldOrg: prev,
      newOrg: next,
    });
    const feature = configurableFeatures.find((f) => f.key === key);
    const nextEnabled = resolveFeatureEffective(key, {
      platformMap: platformFlags(),
      org: next,
    }).enabled;
    const softAffected = nextEnabled
      ? []
      : dependentsOf(key, graph)
          .filter((n) =>
            n.requires.some((clause) =>
              parseRequireClause(clause).some((a) => a.key === key && a.soft),
            ),
          )
          .map((n) => n.name);
    setPendingChange({
      choice,
      key,
      name: feature?.name ?? key,
      nextEnabled,
      status: feature?.status,
      since: feature?.since,
      implications: feature?.implications,
      violations,
      softAffected,
    });
  };

  const handleSave = async (e: Event) => {
    e.preventDefault();
    const db = drizzle();
    if (!db) {
      return;
    }
    setSaving(true);
    try {
      // CC-59: ONLY what's touched is asserted. What the form shows without
      // having been edited might be a code default (the row may not have
      // arrived yet), and asserting it would write it over the server's.
      const dirty = touched();
      const input: OrgSettingsInput = {};
      if (dirty.has("country")) {
        input.country = country();
      }
      if (dirty.has("timezone")) {
        input.timezone = timezone();
      }
      if (dirty.has("locale")) {
        input.locale = locale();
      }
      if (dirty.has("currency_primary")) {
        input.currency_primary = currencyPrimary();
      }
      if (dirty.has("currency_secondary")) {
        input.currency_secondary = currencySecondary() || null;
      }
      if (dirty.has("grading_model_id")) {
        input.grading_model_id = gradingModelId() || null;
      }
      if (dirty.has("attendance_alert_threshold")) {
        input.attendance_alert_threshold = Number(threshold()) || 3;
      }
      if (dirty.has("features")) {
        // DELIBERATE clearing: leaving every override at "Default" sends
        // `{}` and the server applies it. What no longer happens is sending
        // it without the user having touched anything.
        input.features = serializeFeatureOverrides(overrides());
      }
      if (dirty.has("branding")) {
        const branding: BrandingShape = {};
        if (publicName().trim()) {
          branding.publicName = publicName().trim();
        }
        if (tagline().trim()) {
          branding.tagline = tagline().trim();
        }
        input.branding = JSON.stringify(branding);
      }
      await saveOrgSettingsMutation(db, props.orgId, input, Boolean(settings.data));
      // Saved: what was asserted is now the server's, so the next row that
      // arrives takes charge of the form again.
      setTouched(new Set<string>());
      showToast(t(msg`Settings saved`), "success");
    } catch (err) {
      console.error("Error saving org settings:", err);
      showToast(t(msg`Error saving settings`), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle class="text-lg font-bold">{t(msg`Organization settings`)}</CardTitle>
        {/* AUTONOMY · Layer 2: visible purpose, not just fields. */}
        <p class="text-sm text-muted-foreground">
          {t(
            msg`These settings apply to your whole academy: local conventions, currencies, default grading scale, absence alerts, public branding and feature overrides. Courses and cohorts can refine some of them.`,
          )}
        </p>
      </CardHeader>
      <CardContent>
        <Show when={!settings.isLoading} fallback={<Skeleton class="h-64 w-full rounded-lg" />}>
          <form onSubmit={handleSave} class="space-y-5" data-testid="org-settings-form">
            <div class="grid gap-4 sm:grid-cols-2">
              <label class="space-y-1.5">
                <span class="text-sm font-medium">
                  {t(msg`Country`)}{" "}
                  <InfoHint>
                    {t(
                      msg`The country pack sets local conventions: ID document label, tax id, phone prefix, date format and currencies. It changes labels across the app.`,
                    )}
                  </InfoHint>
                </span>
                <Select
                  aria-label={t(msg`Country`)}
                  value={country()}
                  onChange={edit("country", setCountry)}
                  options={listCountryPacks().map((p) => ({
                    value: p.code.toUpperCase(),
                    label: p.name,
                  }))}
                />
              </label>
              <label class="space-y-1.5">
                <span class="text-sm font-medium">{t(msg`Time zone`)}</span>
                <TextField
                  value={timezone()}
                  onInput={(e) => edit("timezone", setTimezone)(e.currentTarget.value)}
                />
              </label>
              <label class="space-y-1.5">
                <span class="text-sm font-medium">{t(msg`Locale`)}</span>
                <TextField
                  value={locale()}
                  onInput={(e) => edit("locale", setLocale)(e.currentTarget.value)}
                />
              </label>
              <label class="space-y-1.5">
                <span class="text-sm font-medium">
                  {t(msg`Grading model`)}{" "}
                  <InfoHint>
                    {t(
                      msg`Default grading model for final grades (courses and cohorts can override it). Manage models in the Grading models tab. Changing it does not rewrite already-sealed grades.`,
                    )}
                  </InfoHint>
                </span>
                <Select
                  aria-label={t(msg`Grading model`)}
                  value={gradingModelId()}
                  onChange={edit("grading_model_id", setGradingModelId)}
                  options={(gradingModels.data ?? []).map((m) => ({
                    value: m.id,
                    label: `${m.name ?? ""} (${modelSummary(m)})`,
                  }))}
                />
              </label>
              <label class="space-y-1.5">
                <span class="text-sm font-medium">{t(msg`Primary currency`)}</span>
                <TextField
                  value={currencyPrimary()}
                  onInput={(e) =>
                    edit(
                      "currency_primary",
                      setCurrencyPrimary,
                    )(e.currentTarget.value.toUpperCase())
                  }
                />
              </label>
              <label class="space-y-1.5">
                <span class="text-sm font-medium">
                  {t(msg`Secondary currency`)}{" "}
                  <InfoHint>
                    {t(
                      msg`Amounts are stored in the primary currency and displayed in both, converted with the exchange rate of the day.`,
                    )}
                  </InfoHint>
                </span>
                <TextField
                  value={currencySecondary()}
                  onInput={(e) =>
                    edit(
                      "currency_secondary",
                      setCurrencySecondary,
                    )(e.currentTarget.value.toUpperCase())
                  }
                />
              </label>
              <label class="space-y-1.5">
                <span class="text-sm font-medium">
                  {t(msg`Absence alert threshold`)}{" "}
                  <InfoHint>
                    {t(
                      msg`Consecutive absences that open a risk alert for a student, visible to admins in the cohort's attendance tab.`,
                    )}
                  </InfoHint>
                </span>
                <TextField
                  type="number"
                  inputmode="numeric"
                  value={threshold()}
                  data-testid="org-threshold-input"
                  onInput={(e) =>
                    edit("attendance_alert_threshold", setThreshold)(e.currentTarget.value)
                  }
                />
              </label>
              {/* AUTONOMY: the rate has ONE single write path (Finance →
                  Rates, with history); here only the last one is reported. */}
              <div class="space-y-1.5">
                <span class="text-sm font-medium">{t(msg`Exchange rate`)}</span>
                <p class="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                  <Show
                    when={typeof settings.data?.exchange_rate === "number"}
                    fallback={t(msg`No rate registered yet.`)}
                  >
                    {String(settings.data?.exchange_rate)} {currencySecondary()}/{currencyPrimary()}
                  </Show>
                </p>
                <Link
                  to="/finance/rates"
                  class="text-xs font-semibold text-primary hover:underline"
                >
                  {t(msg`Manage the daily rate in Finance → Rates`)}
                </Link>
              </div>
            </div>

            {/* ── AUTONOMY: academy branding ── */}
            <div class="space-y-3 rounded-xl border border-border/60 p-4">
              <h3 class="text-sm font-bold">{t(msg`Branding`)}</h3>
              <p class="text-xs text-muted-foreground">
                {t(
                  msg`Public name and tagline of your academy, used on public pages and printable receipts. Leave empty to use the organization name.`,
                )}
              </p>
              <div class="grid gap-4 sm:grid-cols-2">
                <label class="space-y-1.5">
                  <span class="text-sm font-medium">{t(msg`Public name`)}</span>
                  <TextField
                    value={publicName()}
                    data-testid="org-branding-public-name"
                    onInput={(e) => edit("branding", setPublicName)(e.currentTarget.value)}
                  />
                </label>
                <label class="space-y-1.5">
                  <span class="text-sm font-medium">{t(msg`Tagline`)}</span>
                  <TextField
                    value={tagline()}
                    onInput={(e) => edit("branding", setTagline)(e.currentTarget.value)}
                  />
                </label>
              </div>
            </div>

            {/* ── AUTONOMY: feature overrides (registry-driven) ── */}
            <div class="space-y-3 rounded-xl border border-border/60 p-4">
              <h3 class="text-sm font-bold">{t(msg`Feature overrides`)}</h3>
              <p class="text-xs text-muted-foreground">
                {t(
                  msg`Enable or disable platform features for this academy. "Default" inherits the platform state; courses can refine course-scoped features further.`,
                )}
              </p>
              <div class="space-y-2" data-testid="org-feature-overrides">
                <For each={configurableFeatures}>
                  {(feature) => (
                    <div
                      class="flex items-center justify-between gap-3 rounded-lg border border-border/40 px-3 py-2"
                      data-testid={`org-feature-row-${feature.key}`}
                    >
                      <div class="min-w-0">
                        <p class="truncate text-sm font-medium">{feature.name}</p>
                        <p
                          class="truncate text-xs text-muted-foreground"
                          title={feature.description}
                        >
                          {feature.description}
                        </p>
                      </div>
                      <Select
                        aria-label={feature.name}
                        value={overrideChoice(feature.key)}
                        onChange={(v) => {
                          setOverrideChoice(feature.key, toOverrideChoice(v));
                        }}
                        options={[
                          { value: "default", label: t(msg`Default`) },
                          { value: "on", label: t(msg`Enabled`) },
                          { value: "off", label: t(msg`Disabled`) },
                        ]}
                      />
                    </div>
                  )}
                </For>
              </div>
            </div>

            <div class="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
              {(() => {
                const { idLabel } = pack();
                const { taxIdLabel } = pack();
                const { phonePrefix } = pack();
                const { dateFormat } = pack();
                return t(msg`${idLabel} · ${taxIdLabel} · ${phonePrefix} · ${dateFormat}`);
              })()}
            </div>
            <Button type="submit" disabled={saving()} data-testid="org-settings-save">
              <Show when={saving()} fallback={t(msg`Save settings`)}>
                {t(msg`Saving...`)}
              </Show>
            </Button>
          </form>
        </Show>
      </CardContent>

      {/* FLAGS: confirmation for each toggle with implications + graph. */}
      <FeatureToggleDialog
        request={pendingChange()}
        graph={graph}
        onConfirm={() => {
          const change = pendingChange();
          setPendingChange(null);
          if (change) {
            applyOverrideChoice(change.key, change.choice);
          }
        }}
        onCancel={() => setPendingChange(null)}
      />
    </Card>
  );
}
